import type { EditorCore, ProjectSource } from "@loumy/contracts";
import { useCallback, useRef } from "react";
import { useEditorStore } from "./editorStore";
import {
  documentGlob,
  documentNameOf,
  documentPath,
  parseDocument,
  serializeDocument,
} from "./project";
import { useProjectStore } from "./projectStore";

// D72 — Multi-Dokument-Host-Logik: verbindet die ProjectSource (D69) mit dem aktiven Editor-Dokument
// (editorStore) über das EditorCore (documentSet/documentSchema/createDocument). Alle Schreibwege gehen
// fail-closed durch das `documentSchema` (kein Persistieren ungültiger Daten). „self" der Daten-Ebene:
// öffnen/wechseln/neu/speichern/löschen/umbenennen — der Editor liefert nur Substantiv + Schema (D72).

const msg = (e: unknown): string => (e instanceof Error ? e.message : String(e));
/** Kompakter, whitespace-unabhängiger Snapshot für den Dirty-Vergleich. */
const snap = (doc: unknown): string => JSON.stringify(doc ?? null);

export interface ProjectHostApi {
  // reaktiver Zustand
  source: ProjectSource | null;
  projectName: string | null;
  documents: string[];
  activeDocument: string | null;
  dirty: boolean;
  busy: boolean;
  error: string | null;
  canWrite: boolean;
  canMutate: boolean;
  // Aktionen
  connect: (source: ProjectSource) => Promise<boolean>;
  closeProject: () => void;
  refresh: () => Promise<void>;
  openDocument: (name: string) => Promise<void>;
  newDocument: (name: string) => Promise<void>;
  saveDocument: () => Promise<void>;
  deleteDocument: (name: string) => Promise<void>;
  renameDocument: (from: string, to: string) => Promise<void>;
}

export function useProjectHost(core: EditorCore): ProjectHostApi {
  const editorId = core.id;
  const docSet = core.documentSet;
  // Monoton steigender Request-Zähler: invalidiert veraltete (langsame) Reads bei schnellem
  // Dokument-Wechsel — ein älterer openDocument darf den inzwischen aktiven Stand nicht überschreiben.
  const reqEpoch = useRef(0);

  const source = useProjectStore((s) => s.source);
  const handle = useProjectStore((s) => s.handle);
  const documents = useProjectStore((s) => s.documents);
  const activeDocument = useProjectStore((s) => s.activeDocument);
  const savedSnapshot = useProjectStore((s) => s.savedSnapshot);
  const busy = useProjectStore((s) => s.busy);
  const error = useProjectStore((s) => s.error);
  const currentDoc = useEditorStore((s) => s.docs[editorId]);

  const dirty = activeDocument !== null && savedSnapshot !== null && snap(currentDoc) !== savedSnapshot;
  const canWrite = Boolean(source?.capabilities.write);
  const canMutate = Boolean(source?.capabilities.write && typeof source?.removeFile === "function");

  const refresh = useCallback(async () => {
    const { source: src } = useProjectStore.getState();
    if (!src || !docSet) return;
    const paths = await src.list(documentGlob(docSet));
    const names = paths
      .map((p) => documentNameOf(docSet, p))
      .filter((n): n is string => n !== null)
      .sort((a, b) => a.localeCompare(b));
    useProjectStore.getState().setDocuments(names);
  }, [docSet]);

  const connect = useCallback(
    async (src: ProjectSource): Promise<boolean> => {
      const ps = useProjectStore.getState();
      ps.setBusy(true);
      ps.setError(null);
      try {
        const h = await src.open();
        ps.setProject(src, h);
        await refresh();
        return true;
      } catch (e) {
        // closeProject setzt error:null → erst schließen, DANN den Grund setzen (sonst verschluckt).
        useProjectStore.getState().closeProject();
        useProjectStore.getState().setError(msg(e));
        return false;
      } finally {
        useProjectStore.getState().setBusy(false);
      }
    },
    [refresh],
  );

  const closeProject = useCallback(() => {
    ++reqEpoch.current; // laufende Opens invalidieren — kein setDoc/setActiveDocument NACH dem Schließen
    useProjectStore.getState().closeProject();
  }, []);

  const openDocument = useCallback(
    async (name: string) => {
      const ps = useProjectStore.getState();
      if (!ps.source || !docSet) return;
      const epoch = ++reqEpoch.current;
      ps.setBusy(true);
      ps.setError(null);
      try {
        const bytes = await ps.source.readFile(documentPath(docSet, name));
        const parsed = parseDocument(bytes, core.documentSchema);
        if (reqEpoch.current !== epoch) return; // veralteter Read (schneller Wechsel) — verwerfen
        useEditorStore.getState().setDoc(editorId, parsed);
        useEditorStore.getState().setSelection(null);
        useProjectStore.getState().setActiveDocument(name, snap(parsed));
        // D102 (Codex-P1): jedes (Re-)Öffnen — auch der externe Sync-Re-Open (D78) desselben Namens —
        // bumpt die openEpoch, damit Key-basierte Editier-Puffer (Blockly) den frischen Stand laden.
        useProjectStore.getState().bumpOpenEpoch();
      } catch (e) {
        if (reqEpoch.current === epoch) ps.setError(`„${name}" konnte nicht geöffnet werden: ${msg(e)}`);
      } finally {
        // busy nur freigeben, wenn kein neuerer Request übernommen hat (dessen finally räumt dann auf).
        if (reqEpoch.current === epoch) useProjectStore.getState().setBusy(false);
      }
    },
    [docSet, core, editorId],
  );

  const newDocument = useCallback(
    async (name: string) => {
      const ps = useProjectStore.getState();
      if (!ps.source || !docSet) return;
      if (!ps.source.capabilities.write) {
        ps.setError("Diese Quelle ist schreibgeschützt.");
        return;
      }
      if (ps.documents.includes(name)) {
        ps.setError(`„${name}" existiert bereits — bitte einen anderen Namen wählen.`);
        return;
      }
      ++reqEpoch.current; // einen evtl. laufenden openDocument invalidieren
      ps.setBusy(true);
      ps.setError(null);
      try {
        // fail-closed: auch das leere Start-Dokument gegen das documentSchema validieren (D12).
        const doc = core.documentSchema.parse(core.createDocument());
        await ps.source.writeFile(documentPath(docSet, name), serializeDocument(doc));
        useEditorStore.getState().setDoc(editorId, doc);
        useEditorStore.getState().setSelection(null);
        useProjectStore.getState().setActiveDocument(name, snap(doc));
        useProjectStore.getState().bumpOpenEpoch(); // D102 — neues Dokument in den Editor geladen
        await refresh();
      } catch (e) {
        ps.setError(`„${name}" konnte nicht angelegt werden: ${msg(e)}`);
      } finally {
        useProjectStore.getState().setBusy(false);
      }
    },
    [docSet, core, editorId, refresh],
  );

  const saveDocument = useCallback(async () => {
    const ps = useProjectStore.getState();
    if (!ps.source || !docSet || !ps.activeDocument) return;
    if (!ps.source.capabilities.write) {
      ps.setError("Diese Quelle ist schreibgeschützt.");
      return;
    }
    ps.setBusy(true);
    ps.setError(null);
    try {
      // Fail-closed: nur ein gegen das documentSchema valides Dokument wird persistiert (D12).
      const validated = core.documentSchema.parse(useEditorStore.getState().docs[editorId]);
      await ps.source.writeFile(documentPath(docSet, ps.activeDocument), serializeDocument(validated));
      useProjectStore.getState().setActiveDocument(ps.activeDocument, snap(validated));
    } catch (e) {
      ps.setError(`Speichern fehlgeschlagen: ${msg(e)}`);
    } finally {
      useProjectStore.getState().setBusy(false);
    }
  }, [docSet, core, editorId]);

  const deleteDocument = useCallback(
    async (name: string) => {
      const ps = useProjectStore.getState();
      if (!ps.source || !docSet) return;
      if (typeof ps.source.removeFile !== "function" || !ps.source.capabilities.write) {
        ps.setError("Diese Quelle unterstützt kein Löschen.");
        return;
      }
      ++reqEpoch.current; // laufenden openDocument invalidieren
      ps.setBusy(true);
      ps.setError(null);
      try {
        await ps.source.removeFile(documentPath(docSet, name));
        if (useProjectStore.getState().activeDocument === name) {
          useEditorStore.getState().setDoc(editorId, core.createDocument());
          useEditorStore.getState().setSelection(null);
          useProjectStore.getState().setActiveDocument(null, null);
        }
        await refresh();
      } catch (e) {
        ps.setError(`„${name}" konnte nicht gelöscht werden: ${msg(e)}`);
      } finally {
        useProjectStore.getState().setBusy(false);
      }
    },
    [docSet, core, editorId, refresh],
  );

  const renameDocument = useCallback(
    async (from: string, to: string) => {
      const ps = useProjectStore.getState();
      if (!ps.source || !docSet) return;
      if (typeof ps.source.removeFile !== "function" || !ps.source.capabilities.write) {
        ps.setError("Diese Quelle unterstützt kein Umbenennen.");
        return;
      }
      if (ps.documents.includes(to)) {
        ps.setError(`„${to}" existiert bereits — Umbenennen würde es überschreiben.`);
        return;
      }
      ++reqEpoch.current; // laufenden openDocument invalidieren
      ps.setBusy(true);
      ps.setError(null);
      try {
        // kopieren (validiert) → altes löschen; kein atomares Rename im Port. Das Ziel ist oben gegen
        // Überschreiben geprüft; der Lösch-Schritt des Quell-Dokuments folgt erst nach erfolgreichem Schreiben.
        const bytes = await ps.source.readFile(documentPath(docSet, from));
        const validated = core.documentSchema.parse(parseDocument(bytes, core.documentSchema));
        await ps.source.writeFile(documentPath(docSet, to), serializeDocument(validated));
        await ps.source.removeFile(documentPath(docSet, from));
        if (useProjectStore.getState().activeDocument === from) {
          useProjectStore.getState().setActiveDocument(to, snap(validated));
        }
        await refresh();
      } catch (e) {
        ps.setError(`Umbenennen fehlgeschlagen: ${msg(e)}`);
      } finally {
        useProjectStore.getState().setBusy(false);
      }
    },
    [docSet, core, refresh],
  );

  return {
    source,
    projectName: handle?.name ?? null,
    documents,
    activeDocument,
    dirty,
    busy,
    error,
    canWrite,
    canMutate,
    connect,
    closeProject,
    refresh,
    openDocument,
    newDocument,
    saveDocument,
    deleteDocument,
    renameDocument,
  };
}
